import { usePostReagentItemApi, usePutReagentItemApi } from 'api/reagent_item'
import { Flex } from 'components/shared/flex'
import { DateField, InputField, SelectField } from 'components/shared/input'
import { GlobalStateContext } from 'contexts/global_state_context'
import { useContext, useState } from 'react'
import { Button, Modal } from 'react-bootstrap'
import { Form, useEffectSkipFirst, useForm } from 'utils/hooks'
import { ApiError } from 'utils/network/api_hooks'
import { ReagentItem, ReagentItemForm } from 'entities/reagent_item'
import moment from 'moment'
import { Reagent } from 'entities/reagent'

export type FormViewProps = {
    form: Form<ReagentItemForm>
    apiError: ApiError
    reagents?: Reagent[]
}

const FormView = ({ form, apiError, reagents }: FormViewProps) => {
    return (
        <>
            {reagents && (
                <SelectField
                    form={form}
                    attr="reagentId"
                    selectItems={reagents.map((r) => {
                        return { label: r.name, value: r.id! }
                    })}
                    label="新たに作成される試薬の種類を選択してください"
                    includeBlank
                />
            )}
            <Flex>
                <InputField form={form} attr="boxNumber" apiError={apiError} label="箱No" required type="number" min={1} />
                <InputField form={form} attr="lot" apiError={apiError} label="Lot" required />
            </Flex>
            {!reagents && (
                <InputField
                    form={form}
                    attr="price"
                    apiError={apiError}
                    label="価格(税抜)"
                    required
                    placeholder="税抜価格を入力してください"
                    type={'number'}
                    min={0}
                />
            )}
            <Flex>
                {!reagents && (
                    <DateField
                        form={form}
                        attr="deliveredDate"
                        apiError={apiError}
                        label="納品日"
                        required
                        maxDate={new Date()}
                        minDate={moment().add('day', -180).toDate()}
                    />
                )}
                <DateField form={form} attr="manufacturedDate" apiError={apiError} label="製造年月日" required />
                <DateField form={form} attr="expiredDate" apiError={apiError} label="使用期限" />
            </Flex>
            <Flex>
                {!reagents && <DateField form={form} attr="openedDate" apiError={apiError} label="開封日" />}
                <DateField form={form} attr="usedDate" apiError={apiError} label="廃棄日" />
            </Flex>
            <InputField form={form} attr="note" apiError={apiError} label="備考" as="textarea" />
        </>
    )
}

type NewReagentItemFormProps = {
    onComplete?: () => void
    reagentId?: number
}

export const NewReagentItemForm = (props: NewReagentItemFormProps): JSX.Element => {
    const globalState = useContext(GlobalStateContext)
    const api = usePostReagentItemApi()
    const form = useForm<ReagentItemForm>({ reagentId: props.reagentId })
    const [show, setShow] = useState<boolean>(false)

    const handleUpdate = () => {
        api.execute(form)
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(api.loading)
        if (api.isSuccess()) {
            if (props.onComplete) {
                setShow(false)
                props.onComplete()
            }
        }
    }, [api.loading])

    const handleClose = () => {
        setShow(false)
    }

    return (
        <>
            <Button
                variant="success"
                onClick={() => {
                    setShow(true)
                }}
            >
                新規登録
            </Button>
            <Modal show={show} onHide={handleClose}>
                <Modal.Header closeButton>
                    <Modal.Title>試薬の登録</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <>
                        <FormView form={form} apiError={api.apiError} />
                    </>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>
                        閉じる
                    </Button>
                    <Button variant="success" onClick={handleUpdate}>
                        登録
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    )
}
type EditReagentItemFormProps = {
    reagentItem: ReagentItem
    show: boolean
    onClose: () => void
    onComplete?: () => void
}

export const EditReagentItemForm = (props: EditReagentItemFormProps): JSX.Element => {
    const globalState = useContext(GlobalStateContext)
    const api = usePutReagentItemApi()
    const form = useForm<ReagentItemForm>(props.reagentItem)

    useEffectSkipFirst(() => {
        form.set(props.reagentItem)
    }, [props.reagentItem])

    const handleUpdate = () => {
        api.execute(form)
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(api.loading)
        if (api.isSuccess()) {
            if (props.onComplete) {
                props.onComplete()
            }
        }
    }, [api.loading])

    return (
        <Modal show={props.show} onHide={props.onClose}>
            <Modal.Header closeButton>
                <Modal.Title>試薬の編集</Modal.Title>
            </Modal.Header>
            <Modal.Body>{form.object.id && <FormView form={form} apiError={api.apiError} />}</Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={props.onClose}>
                    閉じる
                </Button>
                <Button variant="success" onClick={handleUpdate}>
                    更新
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

type MergeReagentItemFormProps = {
    form: Form<ReagentItemForm>
    show: boolean
    onClose: () => void
    onComplete?: () => void
    reagents: Reagent[]
}

export const MergeReagentItemForm = (props: MergeReagentItemFormProps): JSX.Element => {
    const globalState = useContext(GlobalStateContext)
    const api = usePostReagentItemApi()
    const { form } = props

    const handleUpdate = () => {
        api.execute(form)
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(api.loading)
        if (api.isSuccess()) {
            if (props.onComplete) {
                props.onComplete()
            }
        }
    }, [api.loading])

    return (
        <Modal show={props.show} onHide={props.onClose}>
            <Modal.Header closeButton>
                <Modal.Title>試薬の結合</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <FormView form={form} apiError={api.apiError} reagents={props.reagents} />
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={props.onClose}>
                    閉じる
                </Button>
                <Button variant="success" onClick={handleUpdate}>
                    更新
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
